import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  edgeLengths,
  getFeatureTypeCode,
  getGarageKindName,
  labelAnchor,
  livingStoriesAbove,
  livingSqft,
  measureSqft,
  polygonCentroid,
  sectionAbbrev,
  SHEET_FILL_COLORS,
} from './tapeUtils';

const NAVY = '#285898';
const GREEN = '#68b848';
const PAPER = '#ffffff';
const W = 1100;
const H = 850;
const JPG_SCALE = 3;
// This sheet is embedded into a larger PDF report that already carries the date and order
// info elsewhere, so it deliberately has no header of its own — the plan area starts right
// under the top accent bar to give the sketch as much room as possible.
const PLAN = { x: 36, y: 20, w: 720, h: 768 };

const fmtDim = n => `${Math.round(n)}'`;

const niceScale = span => {
  const raw = span / 5;
  const steps = [5, 10, 20, 25, 40, 50, 100];
  return steps.find(s => s >= raw) ?? 100;
};

const foundationSummary = (features, tapeConfig) => {
  const dwell = features.filter(f => {
    const typeCode = getFeatureTypeCode(f.tapeFeatureTypeId, tapeConfig);
    const garageKindName = getGarageKindName(f.tapeGarageKindId, tapeConfig);
    return typeCode === 'DWL' || livingSqft(f, typeCode, garageKindName) > 0;
  });
  if (!dwell.length) {
    return '—';
  }
  const codes = dwell.map(f => (tapeConfig?.foundationTypes || []).find(x => x.id === f.tapeFoundationTypeId)?.code || '');
  return [...new Set(codes.filter(Boolean))].join(', ');
};

const legendNote = (feature, typeCode, garageKindName) => {
  if (typeCode === 'DWL' && feature.isCathedral) {
    return 'Cathedral, 1 st living';
  }
  if (typeCode === 'DWL') {
    return `${feature.stories} story`;
  }
  if (typeCode === 'GAR') {
    if (garageKindName === 'Half') {
      return 'Half story over garage';
    }
    if (garageKindName === 'Built-in') {
      return 'Built-in garage';
    }
    if (garageKindName === 'Basement') {
      return 'Basement garage';
    }
    return livingStoriesAbove(feature, typeCode, garageKindName) ? 'Living over garage' : 'Attached garage';
  }
  if (typeCode === 'POR') {
    return livingStoriesAbove(feature, typeCode, garageKindName) ? 'Porch + living' : 'Porch';
  }
  if (typeCode === 'BAL') {
    return livingStoriesAbove(feature, typeCode, garageKindName) ? 'Balcony + living' : 'Balcony';
  }
  return typeCode;
};

const legendRows = (features, tapeConfig) => {
  const seen = new Set();
  const rows = [];
  for (const f of features) {
    const typeCode = getFeatureTypeCode(f.tapeFeatureTypeId, tapeConfig);
    const garageKindName = getGarageKindName(f.tapeGarageKindId, tapeConfig);
    const foundationCode = (tapeConfig?.foundationTypes || []).find(x => x.id === f.tapeFoundationTypeId)?.code || '';
    const code = sectionAbbrev(f, typeCode, foundationCode, garageKindName);
    if (seen.has(code)) {
      continue;
    }
    seen.add(code);
    rows.push({ code, note: legendNote(f, typeCode, garageKindName) });
  }
  return rows;
};

export const TapeSketchSheet = forwardRef(({ features, tapeConfig }, ref) => {
  const svgRef = useRef(null);

  const drawn = useMemo(
    () =>
      features
        .map(f => ({ f, pts: f.vertices || [], lens: edgeLengths(f.vertices || []) }))
        .filter(d => d.pts.length >= 3),
    [features],
  );

  const bounds = useMemo(() => {
    const pts = drawn.flatMap(d => d.pts);
    if (!pts.length) {
      return { minX: 0, minY: 0, maxX: 40, maxY: 40 };
    }
    const xs = pts.map(p => p.x);
    const ys = pts.map(p => p.y);
    const pad = 6;
    return {
      minX: Math.min(...xs) - pad,
      minY: Math.min(...ys) - pad,
      maxX: Math.max(...xs) + pad,
      maxY: Math.max(...ys) + pad,
    };
  }, [drawn]);

  const spanX = Math.max(bounds.maxX - bounds.minX, 8);
  const spanY = Math.max(bounds.maxY - bounds.minY, 8);
  const scale = Math.min(PLAN.w / spanX, PLAN.h / spanY);
  const ox = PLAN.x + (PLAN.w - spanX * scale) / 2;
  const oy = PLAN.y + PLAN.h - (PLAN.h - spanY * scale) / 2;

  const toScreen = p => ({
    x: ox + (p.x - bounds.minX) * scale,
    y: oy - (p.y - bounds.minY) * scale,
  });

  const living = features.reduce((s, f) => {
    const typeCode = getFeatureTypeCode(f.tapeFeatureTypeId, tapeConfig);
    const garageKindName = getGarageKindName(f.tapeGarageKindId, tapeConfig);
    return s + livingSqft(f, typeCode, garageKindName);
  }, 0);
  const legend = legendRows(features, tapeConfig);
  const scaleBarFt = niceScale(spanX);

  const renderJpg = async (opts = {}) => {
    const svg = svgRef.current;
    if (!svg) {
      return undefined;
    }
    const jpgScale = opts.scale ?? JPG_SCALE;
    const quality = opts.quality ?? 0.92;
    const clone = svg.cloneNode(true);
    clone.setAttribute('width', String(W));
    clone.setAttribute('height', String(H));
    const xml = new XMLSerializer().serializeToString(clone);
    const svgUrl = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml;charset=utf-8' }));
    const blob = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(W * jpgScale);
        canvas.height = Math.round(H * jpgScale);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('canvas'));
          return;
        }
        ctx.fillStyle = PAPER;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(svgUrl);
        canvas.toBlob(out => (out ? resolve(out) : reject(new Error('jpg'))), 'image/jpeg', quality);
      };
      img.onerror = () => reject(new Error('svg'));
      img.src = svgUrl;
    });
    return blob;
  };

  useImperativeHandle(ref, () => ({ renderJpg }));

  return (
    <svg
      ref={svgRef}
      id="tape-sketch-sheet"
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      style={{ width: '100%', height: 'auto', maxWidth: 900, background: '#fff' }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width={W} height={H} fill={PAPER} />

      <rect x={PLAN.x} y={PLAN.y + 16} width={PLAN.w} height={PLAN.h - 16} fill="#fbfaf6" stroke="#c8c4ba" strokeWidth="0.75" />

      {drawn.map(({ f, pts, lens }) => {
        const typeCode = getFeatureTypeCode(f.tapeFeatureTypeId, tapeConfig);
        const garageKindName = getGarageKindName(f.tapeGarageKindId, tapeConfig);
        const foundationCode = (tapeConfig?.foundationTypes || []).find(x => x.id === f.tapeFoundationTypeId)?.code || '';
        const screenPts = pts.map(toScreen);
        const d = `${screenPts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')} Z`;
        const c = polygonCentroid(screenPts);
        const others = drawn.filter(d0 => d0.f.id !== f.id).map(d0 => d0.pts.map(toScreen));
        const lab = labelAnchor(screenPts, others);
        const fp = Math.round(measureSqft(f.vertices));
        const liv = Math.round(livingSqft(f, typeCode, garageKindName));
        const fill = SHEET_FILL_COLORS[typeCode] || SHEET_FILL_COLORS.OTH;
        const areaLine = liv ? `${fp.toLocaleString()} SF  ·  GLA ${liv.toLocaleString()}` : `${fp.toLocaleString()} SF`;
        return (
          <g key={f.id}>
            <path d={d} fill={fill} stroke="#1a1c1e" strokeWidth="1.6" />
            {pts.map((a, i) => {
              const b = pts[(i + 1) % pts.length];
              const sa = toScreen(a);
              const sb = toScreen(b);
              const mx = (sa.x + sb.x) / 2;
              const my = (sa.y + sb.y) / 2;
              const dx = sb.x - sa.x;
              const dy = sb.y - sa.y;
              const len = Math.hypot(dx, dy) || 1;
              const nx = (-dy / len) * 11;
              const ny = (dx / len) * 11;
              const out = (c.x - mx) * nx + (c.y - my) * ny > 0 ? -1 : 1;
              const tx = mx + nx * out;
              const ty = my + ny * out;
              if (lens[i] < 2) {
                return null;
              }
              return (
                <text key={i} x={tx} y={ty} textAnchor="middle" dominantBaseline="middle" fill="#1a1c1e" fontFamily="IBM Plex Mono, ui-monospace, monospace" fontSize="10">
                  {fmtDim(lens[i])}
                </text>
              );
            })}
            <text x={lab.x} y={lab.y} textAnchor="middle" dominantBaseline="middle" fill="#1a1c1e" fontFamily="IBM Plex Sans, Helvetica, Arial, sans-serif">
              <tspan x={lab.x} dy="-0.7em" fontSize="14" fontWeight="600" fontFamily="IBM Plex Mono, ui-monospace, monospace">
                {sectionAbbrev(f, typeCode, foundationCode, garageKindName)}
              </tspan>
              <tspan x={lab.x} dy="1.45em" fontSize="11" fontFamily="IBM Plex Mono, ui-monospace, monospace">
                {areaLine}
              </tspan>
            </text>
          </g>
        );
      })}

      {drawn.length === 0 && (
        <text x={PLAN.x + PLAN.w / 2} y={PLAN.y + PLAN.h / 2} textAnchor="middle" fill="#8a8680" fontFamily="IBM Plex Sans, Helvetica, Arial, sans-serif" fontSize="14">
          Sketch dwelling, garage, and porch to populate this sheet.
        </text>
      )}

      <g transform={`translate(${PLAN.x + 16}, ${PLAN.y + PLAN.h - 10})`}>
        <line x1="0" y1="-6" x2={scaleBarFt * scale} y2="-6" stroke="#1a1c1e" strokeWidth="1.5" />
        <line x1="0" y1="-11" x2="0" y2="-1" stroke="#1a1c1e" />
        <line x1={scaleBarFt * scale} y1="-11" x2={scaleBarFt * scale} y2="-1" stroke="#1a1c1e" />
        <text x={(scaleBarFt * scale) / 2} y="8" textAnchor="middle" fill="#1a1c1e" fontFamily="IBM Plex Mono, ui-monospace, monospace" fontSize="10">
          {scaleBarFt}'
        </text>
      </g>

      <g transform="translate(780, 40)">
        <text fill="#1a1c1e" fontFamily="IBM Plex Sans, Helvetica, Arial, sans-serif" fontSize="12" letterSpacing="1.6">
          AREA SCHEDULE
        </text>
        <line x1="0" y1="12" x2="288" y2="12" stroke="#1a1c1e" />
        {['Code', 'St', 'SF', 'GLA'].map((h, i) => (
          <text key={h} x={[0, 148, 188, 244][i]} y="30" fill="#4a4e52" fontFamily="IBM Plex Sans, Helvetica, Arial, sans-serif" fontSize="10">
            {h}
          </text>
        ))}
        {features.map((f, i) => {
          const typeCode = getFeatureTypeCode(f.tapeFeatureTypeId, tapeConfig);
          const garageKindName = getGarageKindName(f.tapeGarageKindId, tapeConfig);
          const foundationCode = (tapeConfig?.foundationTypes || []).find(x => x.id === f.tapeFoundationTypeId)?.code || '';
          const y = 50 + i * 25;
          const fp = Math.round(measureSqft(f.vertices));
          const liv = Math.round(livingSqft(f, typeCode, garageKindName));
          return (
            <g key={f.id}>
              <rect x="0" y={y - 13} width="13" height="13" fill={SHEET_FILL_COLORS[typeCode] || SHEET_FILL_COLORS.OTH} stroke="#1a1c1e" />
              <text x="19" y={y - 2} fill="#1a1c1e" fontFamily="IBM Plex Mono, ui-monospace, monospace" fontSize="13">
                {sectionAbbrev(f, typeCode, foundationCode, garageKindName)}
              </text>
              <text x="148" y={y - 2} fill="#1a1c1e" fontFamily="IBM Plex Mono, ui-monospace, monospace" fontSize="13">
                {typeCode === 'GAR' || typeCode === 'POR' || typeCode === 'BAL' ? livingStoriesAbove(f, typeCode, garageKindName) : f.isCathedral ? 'CATH' : f.stories}
              </text>
              <text x="188" y={y - 2} fill="#1a1c1e" fontFamily="IBM Plex Mono, ui-monospace, monospace" fontSize="13">
                {fp.toLocaleString()}
              </text>
              <text x="244" y={y - 2} fill="#1a1c1e" fontFamily="IBM Plex Mono, ui-monospace, monospace" fontSize="13">
                {liv ? liv.toLocaleString() : '—'}
              </text>
            </g>
          );
        })}
        <line x1="0" y1={50 + features.length * 25} x2="288" y2={50 + features.length * 25} stroke="#1a1c1e" />
        <text x="0" y={76 + features.length * 25} fill="#1a1c1e" fontFamily="IBM Plex Sans, Helvetica, Arial, sans-serif" fontSize="13" fontWeight="600">
          Gross living area
        </text>
        <text x="288" y={76 + features.length * 25} textAnchor="end" fill="#1a1c1e" fontFamily="IBM Plex Mono, ui-monospace, monospace" fontSize="20" fontWeight="600">
          {Math.round(living).toLocaleString()} SF
        </text>
        <text x="0" y={118 + features.length * 25} fill="#4a4e52" fontFamily="IBM Plex Sans, Helvetica, Arial, sans-serif" fontSize="11">
          Foundation
        </text>
        <text x="0" y={136 + features.length * 25} fill="#1a1c1e" fontFamily="IBM Plex Sans, Helvetica, Arial, sans-serif" fontSize="13">
          {foundationSummary(features, tapeConfig)}
        </text>
        <text x="0" y={170 + features.length * 25} fill={NAVY} fontFamily="IBM Plex Sans, Helvetica, Arial, sans-serif" fontSize="10" fontWeight="600" letterSpacing="2">
          KEY
        </text>
        <line x1="0" y1={177 + features.length * 25} x2="36" y2={177 + features.length * 25} stroke={GREEN} strokeWidth="2" />
        {legend.map((row, i) => {
          const col = i % 2;
          const r = Math.floor(i / 2);
          const x = col * 148;
          const y = 199 + features.length * 25 + r * 20;
          return (
            <g key={`${row.code}-${i}`}>
              <text x={x} y={y} fill={NAVY} fontFamily="IBM Plex Mono, ui-monospace, monospace" fontSize="10" fontWeight="600">
                {row.code}
              </text>
              <text x={x + 46} y={y} fill="#5a6570" fontFamily="IBM Plex Sans, Helvetica, Arial, sans-serif" fontSize="10">
                {row.note}
              </text>
            </g>
          );
        })}
        <text
          x="0"
          y={205 + features.length * 25 + Math.ceil(Math.max(legend.length, 1) / 2) * 20}
          fill="#5a6570"
          fontFamily="IBM Plex Sans, Helvetica, Arial, sans-serif"
          fontSize="9"
          letterSpacing="0.4"
        >
          S slab · CR crawl · B basement · P pier
        </text>
      </g>
    </svg>
  );
});

TapeSketchSheet.propTypes = {
  features: PropTypes.array,
  tapeConfig: PropTypes.object,
};

export default TapeSketchSheet;
